import * as React from 'react'
import { useMemo } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  BlockedPeriodPolicyFragment,
  DeleteBlockedPeriodPolicyDocument,
  DeleteBlockedPeriodPolicyMutation,
  FetchTimeoffDataDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  policyId: string
  policies: BlockedPeriodPolicyFragment[]
  onClose: () => void
}

const DeleteBlockedPeriodModal = ({ policyId, policies, onClose }: Props) => {
  const policy = useMemo<BlockedPeriodPolicyFragment>(() => {
    const findPolicy = policies.find((p) => p.id == policyId)

    if (!findPolicy) throw new Error(`Missing policy with id: ${policyId}`)

    return findPolicy
  }, [policyId, policies])

  const [deleteBlockedPeriodPolicyMutation] = useMutation(DeleteBlockedPeriodPolicyDocument, {
    extractorFn: (mutationData: DeleteBlockedPeriodPolicyMutation) =>
      mutationData.timeoffMutations.deleteTimeoffBlockedPeriod,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!policy) return

    const result = await deleteBlockedPeriodPolicyMutation({
      variables: { id: policy.id },
      refetchQueries: [FetchTimeoffDataDocument],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      closePortal()
    }

    return handled
  }

  return (
    <Modal
      illustration="delete-cross"
      title={i18n.t('settings.timeoff_blocked_periods.delete.title')}
      description={i18n.t('settings.timeoff_blocked_periods.delete.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button type="negative" submit>
          {i18n.t('common.destroy')}
        </Button>
      }
    />
  )
}

export default DeleteBlockedPeriodModal
